import type { Product, Discount } from '@/payload-types'

import { Link } from '@/i18n/routing'
import React from 'react'
import clsx from 'clsx'
import { Media } from '@/components/Media'
import { Price } from '@/components/Price'
import { SaleBadge } from '@/components/product/SaleBadge'
import {
  ProductBanner,
  VariantBanner,
  BulkDiscountBanner,
  DiscountRibbon,
} from '@/components/product/ProductBanners'
import { getProductDiscountInfo, calculateDiscountedPrice } from '@/lib/productDiscounts'
import styles from '@/styles/liquid-glass.module.css'
import { ShoppingCart } from 'lucide-react'
import { WishlistButton } from '@/components/WishlistButton'

type Props = {
  product: Partial<Product>
  activeDiscounts?: Discount[]
  isLoggedIn?: boolean
  locale?: string
  priority?: boolean
}

export const ProductGridItem: React.FC<Props> = ({ product, activeDiscounts, isLoggedIn, locale, priority }) => {
  const { gallery, basePrice, title, variants } = product

  // Calculate price from basePrice or variant prices
  const price = basePrice
  const hasVariants = Array.isArray(variants) && variants.length > 0

  let lowestAmount = 0
  let highestAmount = 0

  if (hasVariants) {
    const allPrices = variants.flatMap(
      (v) =>
        v.values?.map((val) => val.price).filter((p): p is number => typeof p === 'number') || [],
    )
    if (allPrices.length > 0) {
      lowestAmount = Math.min(...allPrices)
      highestAmount = Math.max(...allPrices)
    }
  }

  // Get discount information
  const discountInfo = getProductDiscountInfo(product, activeDiscounts)

  const metaImage =
    product.meta?.image && typeof product.meta.image !== 'string' ? product.meta.image : undefined

  const image = metaImage || false

  return (
    <Link
      data-testid="product-grid-item"
      data-product-id={product.id}
      className="relative flex flex-col h-full w-full group transition-all duration-300 ease-out hover:-translate-y-1.5"
      href={`/products/${product.slug}`}
    >
      <div
        className={clsx(
          'relative flex flex-col flex-1 overflow-hidden rounded-2xl',
          'bg-[var(--card-bg)]',
          // Light cards are 92% opaque, so the blur only costs GPU there; keep it
          // for the dark theme where the card is mostly transparent (audit #18).
          'dark:backdrop-blur-[18px] dark:saturate-[1.35]',
          'ring-1 ring-[var(--card-border)]',
          'shadow-[0_3px_18px_rgba(0,0,0,0.22),inset_0_1px_0_rgba(255,255,255,0.28),inset_0_-1px_0_rgba(255,255,255,0.05)]',
          'group-hover:ring-[var(--card-border-hover)]',
          'group-hover:shadow-[0_0_0_1.5px_rgba(255,255,255,0.10),0_18px_52px_rgba(0,0,0,0.32),inset_0_1.5px_0_rgba(255,255,255,0.55),inset_0_-1px_0_rgba(255,255,255,0.10)]',
          'transition-all duration-300',
        )}
      >
        <div
          className={clsx(styles.liquidGlassContainer, 'transition-all duration-300')}
          style={{
            padding: '0.75rem',
            background: 'transparent',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            border: 'none',
            borderRadius: '0',
            boxShadow: 'none',
          }}
        >
          {image ? (
            <div className="relative aspect-square">
              <Media
                className={clsx(
                  'relative aspect-square object-cover rounded-xl bg-transparent',
                  styles.liquidGlassImage,
                )}
                height={80}
                imgClassName={clsx('h-full w-full object-cover rounded-xl', {
                  'transition duration-300 ease-in-out group-hover:scale-105': true,
                })}
                priority={priority}
                resource={image}
                size="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                width={80}
              />
              <WishlistButton
                productId={String(product.id)}
                variant="icon"
                isLoggedIn={isLoggedIn}
                loginHref={locale ? `/${locale}/login` : undefined}
              />
            </div>
          ) : (
            <div className="relative aspect-square rounded-xl bg-[var(--card-border)] flex items-center justify-center">
              <div className="text-[var(--card-muted)] text-4xl">📦</div>
              <WishlistButton
                productId={String(product.id)}
                variant="icon"
                isLoggedIn={isLoggedIn}
                loginHref={locale ? `/${locale}/login` : undefined}
              />
            </div>
          )}

          <div className="absolute z-10 bottom-3 right-3 opacity-100 transition-opacity duration-200 pointer-events-none">
            <div
              className="flex items-center justify-center gap-1.5 h-9 w-9 rounded-full md:h-11 md:w-auto md:px-3 md:rounded-lg text-sm font-mono uppercase tracking-wider text-[var(--card-cta-fg)] bg-[var(--card-cta-bg)] group-hover:bg-[var(--card-cta-bg-hover)] shadow-lg transition-colors"
              aria-hidden="true"
            >
              <ShoppingCart className="w-4 h-4" />
              <span className="sr-only md:not-sr-only">View</span>
            </div>
          </div>

          <ProductBanner>
            {discountInfo.hasVariants && <VariantBanner />}
            {discountInfo.hasBulkDiscount && (
              <BulkDiscountBanner
                tierQty={discountInfo.tierQty ?? discountInfo.badge?.tierQty}
                tierPrice={discountInfo.tierPrice ?? discountInfo.badge?.tierPrice}
              />
            )}
          </ProductBanner>

          {(() => {
            const ribbonPercent =
              (discountInfo.hasManualDiscount ? discountInfo.manualDiscountPercentage : undefined) ??
              (discountInfo.badge?.kind === 'automatic' ? discountInfo.badge.percent : undefined)
            return ribbonPercent ? <DiscountRibbon discountPercentage={ribbonPercent} /> : null
          })()}
        </div>

        <div
          className={clsx(
            'relative font-mono flex flex-col items-start text-left md:items-center md:text-center gap-1.5',
            'px-3 pt-3 pb-3.5',
            'border-t border-[var(--card-border)] group-hover:border-[var(--card-border-hover)]',
            'bg-transparent transition-colors duration-300',
          )}
        >
          <div className="text-[var(--card-fg)] font-semibold text-sm tracking-wide transition-colors leading-snug px-1 line-clamp-2 md:line-clamp-none">
            {title}
          </div>

          {!hasVariants && typeof price === 'number' && price > 0 ? (() => {
            // Calculate discounted price for manual discounts on non-variant products
            const hasDiscount = discountInfo.hasManualDiscount && discountInfo.manualDiscountPercentage
            const discountedPrice = hasDiscount
              ? calculateDiscountedPrice(price, discountInfo.manualDiscountPercentage!)
              : price

            return (
              <div className="flex items-center justify-start md:justify-center gap-2 flex-wrap text-emerald-700 dark:text-emerald-400 font-bold text-base">
                <Price
                  amount={discountedPrice}
                  originalAmount={hasDiscount ? price : undefined}
                />
                {product.onSale && <SaleBadge />}
              </div>
            )
          })() : hasVariants && lowestAmount > 0 ? (() => {
            const hasDiscount = Boolean(discountInfo.hasManualDiscount && discountInfo.manualDiscountPercentage)
            const discountedLowest = hasDiscount
              ? calculateDiscountedPrice(lowestAmount, discountInfo.manualDiscountPercentage!)
              : lowestAmount
            const discountedHighest = hasDiscount
              ? calculateDiscountedPrice(highestAmount, discountInfo.manualDiscountPercentage!)
              : highestAmount

            return (
              <div className="flex items-center justify-start md:justify-center gap-2 flex-wrap text-emerald-700 dark:text-emerald-400 font-bold text-base">
                {highestAmount > lowestAmount ? (
                  <Price lowestAmount={discountedLowest} highestAmount={discountedHighest} />
                ) : (
                  <Price
                    amount={discountedLowest}
                    originalAmount={hasDiscount ? lowestAmount : undefined}
                  />
                )}
                {product.onSale && <SaleBadge />}
              </div>
            )
          })() : typeof price === 'number' && price > 0 ? (() => {
            const hasDiscount = discountInfo.hasManualDiscount && discountInfo.manualDiscountPercentage
            const discountedPrice = hasDiscount
              ? calculateDiscountedPrice(price, discountInfo.manualDiscountPercentage!)
              : price

            return (
              <div className="flex items-center justify-start md:justify-center gap-2 flex-wrap text-emerald-700 dark:text-emerald-400 font-bold text-base">
                <Price
                  amount={discountedPrice}
                  originalAmount={hasDiscount ? price : undefined}
                />
                {product.onSale && <SaleBadge />}
              </div>
            )
          })() : (
            <div className="text-[var(--card-muted)] text-xs">Price not available</div>
          )}
        </div>
      </div>
    </Link>
  )
}
